import React, { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { Team, AGE_GROUPS, generateSeasonOptions, getTeamFormatInfo, TeamFormation } from '../types';
import { teamService } from '../services/teamService';
import { assessmentService } from '../services/assessmentService';
import { InviteCoachForm } from './InviteCoachForm';
import { FormationSelector } from './FormationSelector';
// Modal components will be defined inline following the existing pattern
import { Button, Label, Input, ErrorMessage, FlexContainer } from '../styles/GlobalStyles';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: var(--spacing-md);
`;

const ModalContent = styled.div`
  background: var(--card-background);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-xl);
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-xl);
`;

const ModalTitle = styled.h2`
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-secondary);
  padding: var(--spacing-xs);
  
  &:hover {
    color: var(--text-primary);
  }
`;

const ModalBody = styled.div`
  margin-bottom: var(--spacing-xl);
`;

const ModalFooter = styled.div`
  border-top: 1px solid var(--border-color);
  padding-top: var(--spacing-lg);
`;

const FormGroup = styled.div`
  margin-bottom: var(--spacing-lg);
`;

const IntervalInfo = styled.div`
  background: #f0f9ff;
  border: 1px solid #0ea5e9;
  border-radius: var(--border-radius-md);
  padding: var(--spacing-md);
  margin-top: var(--spacing-sm);
  font-size: 0.875rem;
  color: #0c4a6e;
  
  &::before {
    content: 'ℹ️';
    margin-right: var(--spacing-xs);
  }
`;

const IntervalWarning = styled.div`
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: var(--border-radius-md);
  padding: var(--spacing-md);
  margin-top: var(--spacing-sm);
  font-size: 0.875rem;
  color: #92400e;
  
  &::before {
    content: '⚠️';
    margin-right: var(--spacing-xs);
  }
`;

const DeleteSection = styled.div`
  border-top: 1px solid var(--border-color);
  padding-top: var(--spacing-lg);
  margin-top: var(--spacing-lg);
`;

const DeleteWarning = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: var(--border-radius-md);
  padding: var(--spacing-md);
  margin-bottom: var(--spacing-md);
  font-size: 0.875rem;
  color: #991b1b;
  
  &::before {
    content: '⚠️';
    margin-right: var(--spacing-xs);
  }
`;

const DeleteTitle = styled.h3`
  color: var(--error-color);
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0 0 var(--spacing-md) 0;
`;

const FileInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
`;

const FileInputLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  cursor: pointer;
  transition: all 0.2s ease;
  color: var(--text-primary);
  font-size: 0.875rem;
  
  &:hover {
    border-color: var(--primary-color);
    background: var(--primary-color-light);
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const LogoPreview = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  background: var(--background-secondary);
  border-radius: var(--border-radius-md);
`;

const LogoPreviewImage = styled.img`
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: var(--border-radius-sm);
  border: 1px solid var(--border-color);
`;

const RemoveLogoButton = styled.button`
  background: none;
  border: none;
  color: var(--error-color);
  cursor: pointer;
  font-size: 0.875rem;
  padding: var(--spacing-xs);
  
  &:hover {
    text-decoration: underline;
  }
`;

const ImageRequirements = styled.div`
  background: #f0f9ff;
  border: 1px solid #0ea5e9;
  border-radius: var(--border-radius-sm);
  padding: var(--spacing-sm);
  margin-top: var(--spacing-xs);
  font-size: 0.75rem;
  color: #0c4a6e;
  line-height: 1.5;
  
  strong {
    display: block;
    margin-bottom: var(--spacing-xs);
  }
  
  ul {
    margin: 0;
    padding-left: var(--spacing-md);
  }
  
  li {
    margin-bottom: 2px;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  background: var(--input-background);
  color: var(--text-primary);
  font-size: 1rem;
  font-family: inherit;
  transition: all 0.2s ease;
  min-height: var(--touch-target);
  cursor: pointer;

  &:hover {
    border-color: var(--primary-color);
  }

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--primary-color-light);
  }

  &.error {
    border-color: var(--error-color);
  }

  &:disabled {
    background: var(--background-secondary);
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const InfoText = styled.div`
  font-size: 0.875rem;
  color: var(--primary-color);
  background: var(--primary-color-light);
  padding: var(--spacing-sm);
  border-radius: var(--border-radius-sm);
  margin-top: var(--spacing-xs);
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  
  &::before {
    content: 'ℹ️';
  }
`;

interface EditTeamFormData {
    name: string;
    ageGroup: string;
    season: string;
    assessmentInterval: number;
}

interface EditTeamModalProps {
    team: Team;
    isOpen: boolean;
    onClose: () => void;
    onTeamUpdated: (updatedTeam: Team) => void;
    onTeamDeleted?: (teamId: string) => void;
}

export const EditTeamModal: React.FC<EditTeamModalProps> = ({
    team,
    isOpen,
    onClose,
    onTeamUpdated,
    onTeamDeleted,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [serverError, setServerError] = useState<string>('');
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string>(team.logoUrl || '');
    const [formations, setFormations] = useState<{
        primaryInPossession?: TeamFormation;
        primaryOutOfPossession?: TeamFormation;
        secondaryInPossession?: TeamFormation;
        secondaryOutOfPossession?: TeamFormation;
    }>({
        primaryInPossession: team.primaryInPossessionFormation,
        primaryOutOfPossession: team.primaryOutOfPossessionFormation,
        secondaryInPossession: team.secondaryInPossessionFormation,
        secondaryOutOfPossession: team.secondaryOutOfPossessionFormation,
    });

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors },
    } = useForm<EditTeamFormData>({
        defaultValues: {
            name: team.name,
            ageGroup: team.ageGroup,
            season: team.season,
            assessmentInterval: team.assessmentInterval,
        },
    });

    // Watch season and ageGroup for FA Future Fit info
    const selectedSeason = watch('season');
    const selectedAgeGroup = watch('ageGroup');

    // Generate season options
    const seasonOptions = useMemo(() => generateSeasonOptions(), []);

    // Get FA Future Fit info if applicable
    const formatInfo = useMemo(() => {
        if (selectedSeason && selectedAgeGroup) {
            return getTeamFormatInfo(selectedAgeGroup, selectedSeason);
        }
        return '';
    }, [selectedSeason, selectedAgeGroup]);

    const watchedInterval = watch('assessmentInterval');
    const intervalChanged = watchedInterval !== team.assessmentInterval;

    const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                setServerError('Please select an image file');
                return;
            }
            
            // Validate file size (max 2MB)
            if (file.size > 2 * 1024 * 1024) {
                setServerError('Image size must be less than 2MB');
                return;
            }

            setLogoFile(file);
            
            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
            setServerError('');
        }
    };

    const handleRemoveLogo = () => {
        setLogoFile(null);
        setLogoPreview(''); // Clear preview to show file input
    };

    const handleDeleteTeam = async () => {
        if (window.confirm('Are you sure you want to delete this team? This action cannot be undone and will remove all players and assessments associated with this team.')) {
            setIsLoading(true);
            setServerError('');

            try {
                await teamService.deleteTeam(team.id);
                if (onTeamDeleted) {
                    onTeamDeleted(team.id);
                }
                onClose();
            } catch (error: any) {
                setServerError(error.response?.data?.error?.message || 'Failed to delete team');
            } finally {
                setIsLoading(false);
            }
        }
    };

    const onSubmit = async (data: EditTeamFormData) => {
        setIsLoading(true);
        setServerError('');

        try {
            const updatedTeam = await teamService.updateTeam(
                team.id,
                {
                    name: data.name,
                    ageGroup: data.ageGroup,
                    season: data.season,
                    assessmentInterval: data.assessmentInterval,
                    primaryInPossessionFormation: formations.primaryInPossession,
                    primaryOutOfPossessionFormation: formations.primaryOutOfPossession,
                    secondaryInPossessionFormation: formations.secondaryInPossession,
                    secondaryOutOfPossessionFormation: formations.secondaryOutOfPossession,
                },
                logoFile || undefined
            );

            // If assessment interval changed, update the assessment schedule
            if (data.assessmentInterval !== team.assessmentInterval) {
                await assessmentService.updateAssessmentScheduleForTeam(
                    team.id,
                    data.assessmentInterval
                );
            }

            onTeamUpdated(updatedTeam);
            onClose();
        } catch (error: any) {
            const errorMessage = error.response?.data?.error?.message || error.message || 'Failed to update team';
            
            // Provide helpful message if S3 is not configured
            if (errorMessage.includes('Failed to upload team logo') || errorMessage.includes('LOGO_UPLOAD_FAILED')) {
                setServerError('Team updated successfully, but logo upload is not yet configured. Your team settings have been saved without the logo.');
            } else {
                setServerError(errorMessage);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const getIntervalDescription = (weeks: number) => {
        if (weeks === 1) return 'Weekly assessments';
        if (weeks === 2) return 'Bi-weekly assessments';
        if (weeks === 4) return 'Monthly assessments';
        if (weeks === 6) return 'Every 6 weeks (recommended)';
        if (weeks === 8) return 'Every 2 months';
        if (weeks === 12) return 'Quarterly assessments';
        if (weeks === 24) return 'Twice yearly assessments';
        if (weeks === 52) return 'Annual assessments';
        return `Every ${weeks} weeks`;
    };

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <ModalOverlay onClick={handleOverlayClick}>
            <ModalContent>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <ModalHeader>
                        <ModalTitle>Edit Team Settings</ModalTitle>
                        <CloseButton onClick={onClose} aria-label="Close">
                            ×
                        </CloseButton>
                    </ModalHeader>

                    <ModalBody>
                        <FormGroup>
                            <Label htmlFor="season">Season *</Label>
                            <Select
                                id="season"
                                disabled={team.userRole === 'COLLABORATING_COACH'}
                                {...register('season', {
                                    required: 'Season is required',
                                })}
                            >
                                <option value="">Select season...</option>
                                {seasonOptions.map((season) => (
                                    <option key={season} value={season}>
                                        {season}
                                    </option>
                                ))}
                            </Select>
                            {errors.season && <ErrorMessage>{errors.season.message}</ErrorMessage>}
                        </FormGroup>

                        <FormGroup>
                            <Label htmlFor="name">Team Name *</Label>
                            <Input
                                id="name"
                                type="text"
                                disabled={team.userRole === 'COLLABORATING_COACH'}
                                {...register('name', {
                                    required: 'Team name is required',
                                    minLength: {
                                        value: 2,
                                        message: 'Team name must be at least 2 characters',
                                    },
                                })}
                            />
                            {errors.name && <ErrorMessage>{errors.name.message}</ErrorMessage>}
                        </FormGroup>

                        <FormGroup>
                            <Label htmlFor="ageGroup">Age Group *</Label>
                            <Select
                                id="ageGroup"
                                disabled={team.userRole === 'COLLABORATING_COACH'}
                                {...register('ageGroup', {
                                    required: 'Age group is required',
                                })}
                            >
                                <option value="">Select age group...</option>
                                {AGE_GROUPS.map((ageGroup) => (
                                    <option key={ageGroup} value={ageGroup}>
                                        {ageGroup}
                                    </option>
                                ))}
                            </Select>
                            {errors.ageGroup && <ErrorMessage>{errors.ageGroup.message}</ErrorMessage>}
                            {formatInfo && <InfoText>{formatInfo}</InfoText>}
                        </FormGroup>

                        <FormGroup>
                            <Label htmlFor="assessmentInterval">Assessment Interval (weeks) *</Label>
                            <Input
                                id="assessmentInterval"
                                type="number"
                                min="1"
                                max="52"
                                disabled={team.userRole === 'COLLABORATING_COACH'}
                                {...register('assessmentInterval', {
                                    required: 'Assessment interval is required',
                                    min: {
                                        value: 1,
                                        message: 'Assessment interval must be at least 1 week',
                                    },
                                    max: {
                                        value: 52,
                                        message: 'Assessment interval cannot exceed 52 weeks',
                                    },
                                    valueAsNumber: true,
                                })}
                            />
                            {errors.assessmentInterval && (
                                <ErrorMessage>{errors.assessmentInterval.message}</ErrorMessage>
                            )}

                            <IntervalInfo>
                                {getIntervalDescription(watchedInterval || 6)}
                            </IntervalInfo>

                            {intervalChanged && (
                                <IntervalWarning>
                                    Changing the assessment interval will affect the scheduling of future assessments for all players in this team. Existing assessments will not be affected.
                                </IntervalWarning>
                            )}
                        </FormGroup>

                        <FormGroup>
                            <Label>Team Formation (Optional)</Label>
                            <FormationSelector
                                ageGroup={selectedAgeGroup}
                                formations={formations}
                                onChange={setFormations}
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>Team Logo (Optional)</Label>
                            <FileInputWrapper>
                                {!logoPreview || logoFile ? (
                                    <>
                                        <FileInputLabel htmlFor="logo">
                                            📷 {logoFile ? 'Change Logo' : team.logoUrl ? 'Update Logo' : 'Choose Logo Image'}
                                        </FileInputLabel>
                                        <HiddenFileInput
                                            id="logo"
                                            type="file"
                                            accept="image/*"
                                            onChange={handleLogoChange}
                                            disabled={team.userRole === 'COLLABORATING_COACH'}
                                        />
                                        <ImageRequirements>
                                            <strong>📋 Image Requirements:</strong>
                                            <ul>
                                                <li>Format: JPG, PNG, or GIF</li>
                                                <li>Max size: 2MB</li>
                                                <li>Recommended: Square image (e.g., 500×500px)</li>
                                                <li>Image will be displayed as-is (no automatic resizing)</li>
                                            </ul>
                                        </ImageRequirements>
                                    </>
                                ) : null}
                                
                                {logoPreview && (
                                    <LogoPreview>
                                        <LogoPreviewImage src={logoPreview} alt="Logo preview" />
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '0.875rem', marginBottom: 'var(--spacing-xs)' }}>
                                                {logoFile ? logoFile.name : 'Current logo'}
                                            </div>
                                            {team.userRole !== 'COLLABORATING_COACH' && (
                                                <RemoveLogoButton type="button" onClick={handleRemoveLogo}>
                                                    {logoFile ? 'Remove' : 'Change Logo'}
                                                </RemoveLogoButton>
                                            )}
                                        </div>
                                    </LogoPreview>
                                )}
                            </FileInputWrapper>
                        </FormGroup>

                        {serverError && <ErrorMessage>{serverError}</ErrorMessage>}

                        {/* Invite Coach Section - Moved to bottom */}
                        <InviteCoachForm
                            teamId={team.id}
                            existingCollaborator={team.collaboratingCoach || null}
                            pendingInvitation={team.pendingInvitation || null}
                            isMainCoach={team.userRole === 'MAIN_COACH' || !team.userRole}
                            onInviteSent={() => {
                                // Optionally refresh team data to show new collaborator
                                onTeamUpdated(team);
                            }}
                            onCollaboratorRemoved={() => {
                                // Refresh team data after removing collaborator
                                const updatedTeam = { ...team, collaboratingCoach: null };
                                onTeamUpdated(updatedTeam);
                            }}
                        />

                        {onTeamDeleted && team.userRole !== 'COLLABORATING_COACH' && (
                            <DeleteSection>
                                <DeleteTitle>Danger Zone</DeleteTitle>
                                <DeleteWarning>
                                    Deleting this team will permanently remove all players, assessments, and data associated with it. This action cannot be undone.
                                </DeleteWarning>
                                <Button
                                    type="button"
                                    $variant="danger"
                                    onClick={handleDeleteTeam}
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Deleting...' : 'Delete Team'}
                                </Button>
                            </DeleteSection>
                        )}
                    </ModalBody>

                    <ModalFooter>
                        <FlexContainer $justify="flex-end" $gap="var(--spacing-md)">
                            <Button type="button" $variant="secondary" onClick={onClose}>
                                Cancel
                            </Button>
                            {team.userRole !== 'COLLABORATING_COACH' && (
                                <Button type="submit" disabled={isLoading}>
                                    {isLoading ? 'Saving...' : 'Save Changes'}
                                </Button>
                            )}
                        </FlexContainer>
                    </ModalFooter>
                </form>
            </ModalContent>
        </ModalOverlay>
    );
};